import type { HomeTopicCard, HomeTopicSectionConfig } from '../homeTopicSections';
import type { QuickServiceTileItem } from '../components/QuickServiceTileSection';
import { resolveHomeItemTileIcon } from './tablerIcon';

/** `null` = CMS still loading (render no tiles). Array = use API data only. */
export type CmsTopicSections = HomeTopicSectionConfig[] | null;

export function resolveHomeTopicSection(
  sectionId: string,
  topicSections: CmsTopicSections,
): HomeTopicSectionConfig | undefined {
  if (topicSections === null) return undefined;
  const wanted = sectionId.toLowerCase();
  return topicSections.find((s) => s.id.toLowerCase() === wanted);
}

function sortByPreferredSlugs<T extends { slug: string; title: string }>(
  items: T[],
  preferredOrder: readonly string[],
): T[] {
  const rank = new Map(preferredOrder.map((slug, index) => [slug, index]));
  return [...items].sort((a, b) => {
    const ar = rank.get(a.slug) ?? 10_000;
    const br = rank.get(b.slug) ?? 10_000;
    if (ar !== br) return ar - br;
    return a.title.localeCompare(b.title);
  });
}

export function buildOrderedQuickTiles(
  sectionId: string,
  tileOrder: readonly string[],
  tileIcons: Record<string, string>,
  topicSections: CmsTopicSections,
  defaultIcon = 'ti-alert-triangle',
): QuickServiceTileItem[] {
  const section = resolveHomeTopicSection(sectionId, topicSections);
  if (!section?.items.length) return [];

  return sortByPreferredSlugs(section.items, tileOrder).map((item) => ({
    categoryId: item.categoryId,
    slug: item.slug,
    title: item.title,
    badge: item.badge,
    badgeType: item.badgeType,
    icon: item.iconKey?.trim()
      ? resolveHomeItemTileIcon(item.iconKey, item.slug, sectionId)
      : tileIcons[item.slug] ?? resolveHomeItemTileIcon(undefined, item.slug, sectionId) ?? defaultIcon,
  }));
}

export function buildOrderedTopicCards(
  sectionId: string,
  tileOrder: readonly string[],
  topicSections: CmsTopicSections,
): HomeTopicCard[] {
  const section = resolveHomeTopicSection(sectionId, topicSections);
  if (!section?.items.length) return [];
  return sortByPreferredSlugs(section.items, tileOrder);
}
